/**
 * Owner: Masa Bayu
 * File: qx-draft-fonts.ts
 * Date:Time: 2026-10-15 21:47
 * Description: Standard free / system font catalog for Writer ribbon.
 */

export type DraftFontOption = {
  readonly label: string;
  /** CSS font-family stack used for default editor face. */
  readonly family: string;
  /** Primary name passed to the draft fontName command. */
  readonly fontName: string;
};

/** Web-safe + SIL OFL / Apache free faces (loaded via Google Fonts when needed). */
export const DRAFT_FONT_OPTIONS: readonly DraftFontOption[] = [
  { label: 'Source Sans 3', fontName: 'Source Sans 3', family: "'Source Sans 3', 'Segoe UI', sans-serif" },
  { label: 'Inter', fontName: 'Inter', family: "Inter, 'Segoe UI', sans-serif" },
  { label: 'Roboto', fontName: 'Roboto', family: "Roboto, 'Segoe UI', sans-serif" },
  { label: 'Open Sans', fontName: 'Open Sans', family: "'Open Sans', 'Segoe UI', sans-serif" },
  { label: 'Lato', fontName: 'Lato', family: "Lato, 'Segoe UI', sans-serif" },
  { label: 'Montserrat', fontName: 'Montserrat', family: "Montserrat, 'Segoe UI', sans-serif" },
  { label: 'Nunito', fontName: 'Nunito', family: "Nunito, 'Segoe UI', sans-serif" },
  { label: 'Nunito Sans', fontName: 'Nunito Sans', family: "'Nunito Sans', 'Segoe UI', sans-serif" },
  { label: 'Work Sans', fontName: 'Work Sans', family: "'Work Sans', 'Segoe UI', sans-serif" },
  { label: 'Rubik', fontName: 'Rubik', family: "Rubik, 'Segoe UI', sans-serif" },
  { label: 'PT Sans', fontName: 'PT Sans', family: "'PT Sans', 'Segoe UI', sans-serif" },
  { label: 'Fira Sans', fontName: 'Fira Sans', family: "'Fira Sans', 'Segoe UI', sans-serif" },
  { label: 'IBM Plex Sans', fontName: 'IBM Plex Sans', family: "'IBM Plex Sans', 'Segoe UI', sans-serif" },
  { label: 'Noto Sans', fontName: 'Noto Sans', family: "'Noto Sans', 'Segoe UI', sans-serif" },
  { label: 'Arial', fontName: 'Arial', family: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', fontName: 'Helvetica', family: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', fontName: 'Verdana', family: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', fontName: 'Tahoma', family: 'Tahoma, Verdana, sans-serif' },
  { label: 'Trebuchet MS', fontName: 'Trebuchet MS', family: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: 'system-ui', fontName: 'system-ui', family: 'system-ui, -apple-system, Segoe UI, sans-serif' },
  { label: 'Merriweather', fontName: 'Merriweather', family: "Merriweather, Georgia, serif" },
  { label: 'Lora', fontName: 'Lora', family: "Lora, Georgia, serif" },
  { label: 'Libre Baskerville', fontName: 'Libre Baskerville', family: "'Libre Baskerville', Georgia, serif" },
  { label: 'EB Garamond', fontName: 'EB Garamond', family: "'EB Garamond', Garamond, serif" },
  { label: 'Cormorant Garamond', fontName: 'Cormorant Garamond', family: "'Cormorant Garamond', Garamond, serif" },
  { label: 'Crimson Text', fontName: 'Crimson Text', family: "'Crimson Text', Georgia, serif" },
  { label: 'Playfair Display', fontName: 'Playfair Display', family: "'Playfair Display', Georgia, serif" },
  { label: 'Source Serif 4', fontName: 'Source Serif 4', family: "'Source Serif 4', Georgia, serif" },
  { label: 'IBM Plex Serif', fontName: 'IBM Plex Serif', family: "'IBM Plex Serif', Georgia, serif" },
  { label: 'Noto Serif', fontName: 'Noto Serif', family: "'Noto Serif', Georgia, serif" },
  { label: 'PT Serif', fontName: 'PT Serif', family: "'PT Serif', Georgia, serif" },
  { label: 'Literata', fontName: 'Literata', family: "Literata, Georgia, serif" },
  { label: 'Newsreader', fontName: 'Newsreader', family: "Newsreader, Georgia, serif" },
  { label: 'Spectral', fontName: 'Spectral', family: "Spectral, Georgia, serif" },
  { label: 'Georgia', fontName: 'Georgia', family: 'Georgia, Times, serif' },
  { label: 'Times New Roman', fontName: 'Times New Roman', family: "'Times New Roman', Times, serif" },
  { label: 'Times', fontName: 'Times', family: 'Times, "Times New Roman", serif' },
  { label: 'Palatino', fontName: 'Palatino', family: 'Palatino, "Palatino Linotype", serif' },
  { label: 'Garamond', fontName: 'Garamond', family: 'Garamond, "EB Garamond", serif' },
  { label: 'IBM Plex Mono', fontName: 'IBM Plex Mono', family: "'IBM Plex Mono', Consolas, monospace" },
  { label: 'Source Code Pro', fontName: 'Source Code Pro', family: "'Source Code Pro', Consolas, monospace" },
  { label: 'Fira Code', fontName: 'Fira Code', family: "'Fira Code', Consolas, monospace" },
  { label: 'JetBrains Mono', fontName: 'JetBrains Mono', family: "'JetBrains Mono', Consolas, monospace" },
  { label: 'Inconsolata', fontName: 'Inconsolata', family: 'Inconsolata, Consolas, monospace' },
  { label: 'Courier New', fontName: 'Courier New', family: "'Courier New', Courier, monospace" },
  { label: 'Courier', fontName: 'Courier', family: 'Courier, "Courier New", monospace' },
  { label: 'Comic Sans MS', fontName: 'Comic Sans MS', family: "'Comic Sans MS', 'Comic Sans', cursive" },
  { label: 'Impact', fontName: 'Impact', family: 'Impact, Haettenschweiler, sans-serif' },
];

export const DEFAULT_DRAFT_FONT = DRAFT_FONT_OPTIONS[0]!;

/** Default drop-cap letter face · independent of body `--qxw-body`. */
export const DEFAULT_DROPCAP_FONT =
  DRAFT_FONT_OPTIONS.find((font) => font.fontName === 'Playfair Display') ??
  DEFAULT_DRAFT_FONT;

/** Google Fonts CSS2 URL for the free webfont faces above (system faces omitted). */
export const DRAFT_GOOGLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?' +
  [
    'family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400',
    'family=Crimson+Text:ital,wght@0,400;0,600;1,400',
    'family=EB+Garamond:ital,wght@0,400;0,600;1,400',
    'family=Fira+Code:wght@400;600',
    'family=Fira+Sans:ital,wght@0,400;0,600;1,400',
    'family=IBM+Plex+Mono:ital,wght@0,400;0,600;1,400',
    'family=IBM+Plex+Sans:ital,wght@0,400;0,600;1,400',
    'family=IBM+Plex+Serif:ital,wght@0,400;0,600;1,400',
    'family=Inconsolata:wght@400;600',
    'family=Inter:ital,wght@0,400;0,600;1,400',
    'family=JetBrains+Mono:ital,wght@0,400;0,600;1,400',
    'family=Lato:ital,wght@0,400;0,700;1,400',
    'family=Libre+Baskerville:ital,wght@0,400;0,700;1,400',
    'family=Literata:ital,opsz,wght@0,7..72,400;0,7..72,600;1,7..72,400',
    'family=Lora:ital,wght@0,400;0,600;1,400',
    'family=Merriweather:ital,opsz,wght@0,18..144,400;0,18..144,700;1,18..144,400',
    'family=Montserrat:ital,wght@0,400;0,600;1,400',
    'family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,600;1,6..72,400',
    'family=Noto+Sans:ital,wght@0,400;0,600;1,400',
    'family=Noto+Serif:ital,wght@0,400;0,600;1,400',
    'family=Nunito:ital,wght@0,400;0,600;1,400',
    'family=Nunito+Sans:ital,wght@0,400;0,600;1,400',
    'family=Open+Sans:ital,wght@0,400;0,600;1,400',
    'family=PT+Sans:ital,wght@0,400;0,700;1,400',
    'family=PT+Serif:ital,wght@0,400;0,700;1,400',
    'family=Playfair+Display:ital,wght@0,400;0,600;1,400',
    'family=Roboto:ital,wght@0,400;0,500;1,400',
    'family=Rubik:ital,wght@0,400;0,600;1,400',
    'family=Source+Code+Pro:ital,wght@0,400;0,600;1,400',
    'family=Source+Sans+3:ital,wght@0,400;0,600;1,400',
    'family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400',
    'family=Spectral:ital,wght@0,400;0,600;1,400',
    'family=Work+Sans:ital,wght@0,400;0,600;1,400',
  ].join('&') +
  '&display=swap';

/** D2 · dipakai pelayar standalone (main.tsx) dan host Studio qxread. */
export function ensureQxwDraftFonts(): void {
  if (document.querySelector('link[data-qxw-draft-fonts]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = DRAFT_GOOGLE_FONTS_HREF;
  link.setAttribute('data-qxw-draft-fonts', '1');
  document.head.appendChild(link);
}
